import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store, BadgeCheck, Tag, Trash2, Camera, Users, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, Spinner, Pill } from "../components/common";
import PhotoGallery from "../components/PhotoGallery";
import { MinisiteEditor } from "../components/Minisite";
import { useAuth } from "../context/AuthContext";

const FIELDS = [
  ["description", "Description", "textarea"], ["phone", "Phone", "text"], ["website", "Website", "text"],
  ["hours", "Opening hours (e.g. Daily 5:00 AM – 10:30 PM)", "text"], ["menu_url", "Menu / catalog URL", "text"],
  ["address", "Business address", "text"], ["contact_person", "Contact person", "text"],
  ["business_email", "Business email", "text"],
];

const ROLE_LABEL = { owner: "Owner", manager: "Manager", editor: "Editor" };

function TeamManager({ biz, onChanged }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const canManage = ["owner", "manager"].includes(biz.my_role);
  const team = biz.team || [];

  const add = async () => {
    if (!email.trim()) return toast.error("Enter the team member's email");
    try {
      await api.post(`/marketplace/businesses/${biz.id}/team`, { email: email.trim(), role });
      toast.success(`${email.trim()} added as ${ROLE_LABEL[role]}`);
      setEmail(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add team member"); }
  };
  const remove = async (m) => {
    try { await api.delete(`/marketplace/businesses/${biz.id}/team/${m.id}`); toast.success(`${m.email} removed`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div className="mt-6 border-t border-zinc-200 pt-4" data-testid={`team-manager-${biz.id}`}>
      <div className="flex items-center gap-2 font-heading font-900"><Users className="w-4 h-4 text-emerald-600" /> Team members</div>
      <p className="text-xs text-muted-foreground mt-1">Managers can edit the profile and manage the team; Editors can edit the profile and publish deals.</p>
      <div className="mt-3 space-y-2">
        {team.map((m) => (
          <div key={m.id} className="flex items-center gap-3 border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm" data-testid={`team-member-${m.id}`}>
            <span className="font-600">{m.name || m.email}</span>
            {m.name && <span className="text-xs text-muted-foreground">{m.email}</span>}
            <Pill tone={m.role === "manager" ? "blue" : "muted"}>{ROLE_LABEL[m.role] || m.role}</Pill>
            {!m.user_id && <span className="text-[11px] text-amber-700">invited — activates when they sign up with this email</span>}
            {canManage && (
              <button onClick={() => remove(m)} className="ml-auto text-red-600 hover:text-red-800" data-testid={`team-remove-${m.id}`} aria-label="Remove team member">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        {!team.length && <p className="text-xs text-muted-foreground">No team members yet — it's just you.</p>}
      </div>
      {canManage && (
        <div className="flex flex-wrap gap-2 mt-3">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com"
            data-testid="team-email-input" className="border border-zinc-300 px-3 py-2 text-sm flex-1 min-w-[200px]" />
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="team-role-select"
            className="border border-zinc-300 bg-white px-3 py-2 text-sm">
            <option value="manager">Manager</option>
            <option value="editor">Editor</option>
          </select>
          <button onClick={add} data-testid="team-add-btn" className="bg-emerald-600 text-white px-4 py-2 text-sm font-700 hover:bg-emerald-500 transition-colors">Add member</button>
        </div>
      )}
    </div>
  );
}

function BizEditor({ biz, onSaved }) {
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map(([k]) => [k, biz[k] || ""])));
  const [deals, setDeals] = useState(null);
  const [deal, setDeal] = useState({ title: "", description: "" });

  const loadDeals = () => api.get(`/marketplace/businesses/${biz.id}`).then((r) => setDeals(r.data.deals)).catch(() => setDeals([]));
  useEffect(() => { loadDeals(); }, [biz.id]); // eslint-disable-line

  const save = async () => {
    try {
      await api.patch(`/marketplace/businesses/${biz.id}`, form);
      toast.success("Profile updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const postDeal = async () => {
    if (deal.title.length < 3) return toast.error("Deal title too short");
    try {
      await api.post(`/marketplace/businesses/${biz.id}/deals`, deal);
      toast.success("Deal published");
      setDeal({ title: "", description: "" }); loadDeals();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeDeal = async (id) => {
    try { await api.delete(`/marketplace/deals/${id}`); loadDeals(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="border border-zinc-300 bg-white p-6" data-testid={`my-business-${biz.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {biz.verified ? <BadgeCheck className="w-5 h-5 text-emerald-600" /> : <ShieldAlert className="w-5 h-5 text-amber-500" />}
        <Link to={`/business/${biz.id}`} className="font-heading font-900 text-xl hover:text-primary transition-colors">{biz.name}</Link>
        {biz.airport_iata && <Pill>{biz.airport_iata}{biz.terminal ? ` · ${biz.terminal}` : ""}</Pill>}
        {biz.verified ? <Pill tone="green">Verified</Pill> : <Pill tone="amber">Unverified</Pill>}
        {biz.my_role && <Pill tone="blue">{ROLE_LABEL[biz.my_role] || biz.my_role}</Pill>}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        {FIELDS.map(([k, label, kind]) => (
          <label key={k} className={kind === "textarea" ? "sm:col-span-2" : ""}>
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
            {kind === "textarea"
              ? <textarea rows={2} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1" data-testid={`edit-${k}`} />
              : <input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1" data-testid={`edit-${k}`} />}
          </label>
        ))}
      </div>
      <button onClick={save} data-testid={`save-business-${biz.id}`} className="mt-3 bg-emerald-700 text-white px-5 py-2 text-sm font-700 hover:bg-emerald-600 transition-colors">Save profile</button>

      <div className="mt-6 border-t border-zinc-200 pt-4">
        <div className="flex items-center gap-2 font-heading font-900"><Tag className="w-4 h-4 text-emerald-600" /> Deals</div>
        {deals === null ? <Spinner /> : (
          <div className="mt-2 space-y-2">
            {deals.map((d) => (
              <div key={d.id} className="flex items-center gap-3 border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm">
                <span className="font-700">{d.title}</span>
                <button onClick={() => removeDeal(d.id)} className="ml-auto text-red-600 hover:text-red-800" data-testid={`remove-deal-${d.id}`} aria-label="Remove deal"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!deals.length && <p className="text-xs text-muted-foreground">No active deals.</p>}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          <input value={deal.title} onChange={(e) => setDeal((d) => ({ ...d, title: e.target.value }))} placeholder="Deal title (e.g. Happy hour 3–5 PM)" data-testid="deal-title-input" className="border border-zinc-300 px-3 py-2 text-sm flex-1 min-w-[200px]" />
          <input value={deal.description} onChange={(e) => setDeal((d) => ({ ...d, description: e.target.value }))} placeholder="Details (optional)" className="border border-zinc-300 px-3 py-2 text-sm flex-1 min-w-[200px]" />
          <button onClick={postDeal} data-testid="deal-publish-btn" className="bg-emerald-600 text-white px-4 py-2 text-sm font-700 hover:bg-emerald-500 transition-colors">Publish deal</button>
        </div>
      </div>

      <div className="mt-6 border-t border-zinc-200 pt-4" data-testid={`my-business-photos-${biz.id}`}>
        <div className="flex items-center gap-2 font-heading font-900 mb-2"><Camera className="w-4 h-4 text-emerald-600" /> Photos</div>
        <p className="text-xs text-muted-foreground mb-3">Show travelers your storefront, food and products — photos appear on your public page after a quick review.</p>
        <PhotoGallery entityType="business" entityId={biz.id} />
      </div>

      <TeamManager biz={biz} onChanged={onSaved} />

      <MinisiteEditor biz={biz} onSaved={onSaved} />
    </div>
  );
}

export default function MyBusiness() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/marketplace/my-businesses").then((r) => setData(r.data)).catch(() => setData({ items: [], claims: [] }));
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line

  if (!user) return (
    <div className="max-w-3xl mx-auto px-5 py-20 text-center">
      <Store className="w-10 h-10 mx-auto mb-3 text-zinc-400" />
      <p className="text-muted-foreground">Please <Link to="/login" className="text-primary underline">sign in</Link> to manage your airport business.</p>
    </div>
  );

  return (
    <div data-testid="my-business-page">
      <PageHeader plain eyebrow="Marketplace" title="My Airport Business"
        subtitle="Manage your business profiles: update details travelers rely on, publish deals, and add team members with Manager or Editor access." />
      <div className="max-w-4xl mx-auto px-5 lg:px-8 pb-16 space-y-5">
        {!data ? <Spinner /> : (
          <>
            {data.items.map((b) => <BizEditor key={b.id} biz={b} onSaved={load} />)}
            {!data.items.length && (
              <div className="border border-dashed border-zinc-300 py-14 text-center text-muted-foreground">
                No businesses yet. Find yours in the <Link to="/marketplace" className="text-primary underline">Airport Marketplace</Link> and claim it, or register one from the <Link to="/register" className="text-primary underline">sign-up page</Link>.
              </div>
            )}
            {data.claims.filter((c) => c.status === "pending").length > 0 && (
              <div className="border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="pending-claims">
                <span className="font-700">Pending claims:</span>{" "}
                {data.claims.filter((c) => c.status === "pending").map((c) => c.business_name).join(", ")} — under review by our team.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
